import { useState, useEffect, Fragment, forwardRef } from "react";
import { Input } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import { TableVirtuoso } from 'react-virtuoso';
import Grid from '@mui/material/Grid2';
import DropZoneArea from './drop-zone-area'
import { resolveColumnLineIndex, CSVToArray } from '../utils/util';
import { getFileContentsAsText, getFileContentsAsDataUrl } from '../utils/file-util';


const MEDIA_TYPES = {
  accept: { 'image/*': ['.jpg', '.jpeg', '.png'] }
};

const TEXT_TYPES = {
  accept: { 'text/*': ['.csv', '.txt'] }
};

const COLUMNS = [
  {
    width: 100,
    label: 'Artist Name',
    dataKey: 'artist',
  },
];

const VirtuosoTableComponents = {
  Scroller: forwardRef((props, ref) => (
    <TableContainer component={Paper} {...props} ref={ref} />
  )),
  Table: (props) => (
    <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} size='small' />
  ),
  TableHead: forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
  TableRow,
  TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
};


export default function LoadingMetadataTable(props) {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState(props.columns ? props.columns : COLUMNS);
  const [containsImageColumns, setContainsImageColumns] = useState(false);
  const [containsTextColumns, setContainsTextColumns] = useState(false);

  const { onUpload } = { ...props };

  function handleUpload(rows) {
    columns
      .filter(a => a.type === 'CALCULATED' && a.fn !== undefined)
      .map(c => c.fn(rows));
    onUpload(rows);
  }

  function rowContent(_index, row) {
    return (
      <Fragment>
        {columns.map((column) => (
          <TableCell
            key={column.dataKey}
            align={column.numeric || false ? 'right' : 'left'}
            onBlur={() => {
              let r = [...rows];
              r.forEach(rr => rr.editing = false);
              handleUpload(r);
              setRows(r);
            }}
            onClick={event => {
              if (event.detail === 2) {
                let r = [...rows];
                r[_index].editing = true;
                handleUpload(r);
                setRows(r);
              }
            }}
          >
            {column.type === 'IMAGE' ?
              (<Avatar src={row.image} sx={{ width: 64, height: 64 }} />) :
              (row.editing ?
                <Input value={row[column.dataKey]} onChange={v => {
                  let r = [...rows];
                  r[_index][column.dataKey] = v.target.value;
                  handleUpload(r);
                  setRows(r);
                }} /> :
                (row[column.dataKey])
              )
            }
          </TableCell>
        ))}
      </Fragment>
    );
  }

  function fixedHeaderContent() {
    return (
      <TableRow>
        {columns.map((column) => (
          <TableCell
            key={column.dataKey}
            variant="head"
            align={column.numeric || false ? 'right' : 'left'}
            style={{ width: column.width }}
            sx={{ backgroundColor: 'background.paper' }}
          >
            {column.label}
          </TableCell>
        ))}
      </TableRow>
    );
  }

  async function handleImageDrop(files) {
    const r = (await Promise.all(files
      .map(async f => {
        return {
          ...f,
          image: await getFileContentsAsDataUrl(f),
          contentType: await f.type,
        }
      })))
      .map((f, idx) => {
        return {
          id: idx,
          path: f.path,
          image: f.image,
          content: f.image.split("base64,")[1],
          contentType: f.contentType,
        };
      });

    handleUpload(r);
    setRows(r);
  }

  async function handleTextDrop(files) {
    const lines = (await getFileContentsAsText(files[0]))
      .split('\n');

    if (lines === null || lines.length === 0) {
      return;
    }

    let usedColumns = resolveColumnLineIndex(columns, lines[0]);

    if (usedColumns === null || usedColumns.length === 0) {
      return;
    }

    const r = [];
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i] || lines[i].trim() === '') {
        continue;
      }

      const data = CSVToArray(lines[i])[0];

      let obj = { id: i, ...rows[i - 1] };
      for (let j = 0; j < usedColumns.length; j++) {
        obj[usedColumns[j].dataKey] = data[usedColumns[j].index];
      }

      r.push(obj);
    }

    handleUpload(r);
    setRows(r);
  };

  useEffect(() => {
    setColumns(props.columns);
  }, [props.columns]);

  useEffect(() => {
    setContainsImageColumns(columns
      .map(a => a.type === 'IMAGE')
      .reduce((a, b) => a || b, false));
    setContainsTextColumns(columns
      .map(a => a.type === 'TEXT')
      .reduce((a, b) => a || b, false));
  }, [columns]);

  return (
    <Grid container spacing={2}>
      {containsImageColumns ?
        (
          <Grid size={containsTextColumns ? 6 : 12}>
            <DropZoneArea message={"Upload media (.jpg or .png)"} accept={MEDIA_TYPES} onDrop={handleImageDrop} />
          </Grid>
        ) : (null)}
      {containsTextColumns ?
        (
          <Grid size={containsImageColumns ? 6 : 12}>
            <DropZoneArea message={"Upload text (.txt or .csv)"} accept={TEXT_TYPES} onDrop={handleTextDrop} />
          </Grid>
        ) : (null)}
      <Grid size={12}>
        <Paper style={{ height: 280, width: '100%' }}>
          <TableVirtuoso
            data={rows}
            components={VirtuosoTableComponents}
            fixedHeaderContent={fixedHeaderContent}
            itemContent={rowContent}
          />
        </Paper>
      </Grid>
    </Grid>
  );
}